import { Suspense, useMemo } from 'react';

import { Box, CircularProgress, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { type QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet, useMatches } from '@tanstack/react-router';
import { Helmet } from 'react-helmet';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { type BackgroundData } from 'typescript/interfaces/BackgroundData';
import { type NonCircularLinkProps } from 'typescript/types/NonCircularLinkProps';

import { Background } from 'components/Background';
import { DefaultError } from 'components/DefaultError';
import { Toaster } from 'components/Toaster';

import { GoogleAnalyticsContextProvider } from 'hooks/useGoogleAnalytics';
import { useRegisterAnalyticsEvents } from 'hooks/useRegisterAnalyticsEvents';
import { useTheme } from 'hooks/useTheme';

import 'locales/index';
import './__root.scss';

const Root = () => {
  const matches = useMatches();

  const theme = useTheme();

  useRegisterAnalyticsEvents();

  const [rootMatch] = useMemo(() => {
    return [...matches].reverse();
  }, [matches]);

  const { title } = useMemo(() => {
    return (
      rootMatch.meta?.find((meta) => {
        return meta?.title;
      }) ?? { title: 'TheSOURCE' }
    );
  }, [rootMatch.meta]);

  return (
    <ErrorBoundary fallback={<DefaultError />}>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>{title}</title>
      </Helmet>

      <CssBaseline />

      {theme && (
        <ThemeProvider theme={theme}>
          <GoogleAnalyticsContextProvider>
            <Background />

            <Suspense
              fallback={
                <Box
                  sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <CircularProgress variant="indeterminate" color="primary" size={100} />
                  <Typography variant="h5" sx={{ marginY: 2 }} color="primary">
                    {'Loading ... '}
                  </Typography>
                </Box>
              }
            >
              <Outlet />

              <Toaster />
            </Suspense>

            {process.env.NODE_ENV === 'development' && <TanStackRouterDevtools position="bottom-right" />}
          </GoogleAnalyticsContextProvider>
        </ThemeProvider>
      )}
    </ErrorBoundary>
  );
};

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  back?: NonCircularLinkProps;
  breadcrumbs: boolean;
  disableButtonBar: boolean;
  hideButtonBarButtons: string[];
  disableSupportButton: boolean;
  background?: BackgroundData;
  title: string | null;
}>()({
  component: Root,
  staticData: {
    title: 'Home',
  },
  errorComponent: DefaultError,
});
